import { useState, useEffect } from "react";
import "./Usage.css"
import { Form, InputNumber, Button, List, Row, Col, message, Slider } from "antd";
import {
  getUserPreferenceConfig,
  updateUserPreferenceConfig,
} from "../../api/userPreference";
import AccessModal from "../../Common/Modal/AccessModal";
import { claudeAdvancedConfigData, deepseekAdvancedConfigData, geminiAdvancedConfigData, openAIAdvancedConfigData } from "../../constants/user-advanced-ai-parameters";
import useRemaining from "../../Hooks/useRemaining";
import { isRemainingExpired } from "../../Utility/utility";
import "./Usage.css";

const AdvanceAiParameters = () => {
  const [form] = Form.useForm();
  const [configData, setConfigData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { remaining, expireDate, userInfo } = useRemaining();

  const getConfigData = async () => {
    try {
      const response = await getUserPreferenceConfig();
      if (response) {
        setConfigData(response);
        form.setFieldsValue(response);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getConfigData();
  }, []);

  const handleUpdateClick = async () => {
    if (!isEditing) {
      setIsEditing(!isEditing);
    }

    if (isEditing) {
      setIsUpdating(true);

      try {
        const values = await form.validateFields();

        const response = await updateUserPreferenceConfig(values);
        if (response) {
          setIsEditing(!isEditing);
          message.success(response?.message);
        } else {
          message.error(response?.message);
        }
      } catch (error) {
        console.log("Failed:", error);
      } finally {
        setIsUpdating(false)
      }
    }
  };

  const handleReset = async () => {
    if (!isEditing) {
      setIsEditing(!isEditing);
    }
    if (isEditing) {
      setIsUpdating(true);

      try {
        const resetConfig = {
          ClaudeAIMaxToken: '',
          claudeAiTemperature: '',
          geminiMaxOutputTokens: '',
          geminiTemperature: '',
          geminiTopK: '',
          geminiTopP: '',
          openAiFrequency_penalty: '',
          openAiMax_tokens: '',
          openAiPresence_penalty: '',
          openAiTemperature: '',
          openAiTopP: '',
          deepseekTemperature: '',
          deepseekMaxTokens: '',
          deepseekTopP: '',
          deepseekTopK: '',
          deepseekRepetitionPenalty: '',
        };

        const response = await updateUserPreferenceConfig(resetConfig);
        if (response) {
          setIsEditing(!isEditing);
          message.success(response?.message);
        } else {
          message.error(response?.message);
        }
      } catch (error) {
        console.log("Failed:", error);
      } finally {
        getConfigData()
        setIsUpdating(false)
      }
    }
  };

  const isDisabled = () => {
    if (userInfo?.role === 'superadmin') return false;

    const isExpired = isRemainingExpired(remaining?.expireDate || expireDate) === true;

    return !remaining?.advance_parameter || isExpired;
  };

  return (
    <>
      <Form
        style={isDisabled() ? { pointerEvents: "none", opacity: "0.4" } : {}}
        form={form}
        initialValues={configData} layout="vertical"
      >
        <div className="scrollable-list">
          <Row gutter={[0, 20]}>
            <Col span={24}>
              <List
                header={<h5>OpenAI</h5>}
                size="small"
                bordered
                dataSource={openAIAdvancedConfigData}
                renderItem={(item) => (
                  <List.Item>
                    <Form.Item
                      style={{ width: '100%' }}
                      className="p-0 m-0"
                      name={item.name}
                      label={
                        <div>
                          {`${item.label} ${!form.getFieldValue(item.name) ? "DEFAULT" : ""}`}
                          {item.subtitle && <div style={{ fontSize: 'smaller', color: '#888' }}>{item.subtitle}</div>}
                        </div>
                      }
                      rules={item.rules}
                    >
                      <Slider
                        min={item.rules[0].min}
                        max={item.rules[0].max}
                        step={item.rules[0].step}
                        disabled={!isEditing}
                        onChange={(value) => form.setFieldsValue({ [item.name]: value })}
                        value={form.getFieldValue(item.name)}
                      />
                    </Form.Item>
                  </List.Item>
                )}
              />
            </Col>
            <Col span={24}>
              <List
                header={<h5>Gemini</h5>}
                size="small"
                bordered
                dataSource={geminiAdvancedConfigData}
                renderItem={(item) => (
                  <List.Item>
                    <Form.Item
                      style={{ width: '100%' }}
                      className="p-0 m-0"
                      name={item.name}
                      label={
                        <div>
                          {`${item.label} ${!form.getFieldValue(item.name) ? "DEFAULT" : ""}`}
                          {item.subtitle && <div style={{ fontSize: 'smaller', color: '#888' }}>{item.subtitle}</div>}
                        </div>
                      }
                      rules={item.rules}
                    >
                      <Slider
                        min={item.rules[0].min}
                        max={item.rules[0].max}
                        step={item.rules[0].step}
                        disabled={!isEditing}
                        onChange={(value) => form.setFieldsValue({ [item.name]: value })}
                        value={form.getFieldValue(item.name)}
                      />
                    </Form.Item>
                  </List.Item>
                )}
              />
            </Col>
            <Col span={24}>
              <List
                header={<h5>Claude</h5>}
                size="small"
                bordered
                dataSource={claudeAdvancedConfigData}
                renderItem={(item) => (
                  <List.Item>
                    <Form.Item
                      style={{ width: '100%' }}
                      className="p-0 m-0"
                      name={item.name}
                      label={
                        <div>
                          {`${item.label} ${!form.getFieldValue(item.name) ? "DEFAULT" : ""}`}
                          {item.subtitle && <div style={{ fontSize: 'smaller', color: '#888' }}>{item.subtitle}</div>}
                        </div>
                      }
                      rules={item.rules}
                    >
                      <Slider
                        min={item.rules[0].min}
                        max={item.rules[0].max}
                        step={item.rules[0].step}
                        disabled={!isEditing}
                        onChange={(value) => form.setFieldsValue({ [item.name]: value })}
                        value={form.getFieldValue(item.name)}
                      />
                    </Form.Item>
                  </List.Item>
                )}
              />
            </Col>
            <Col span={24}>
              <List
                header={<h5>Deepseek</h5>}
                size="small"
                bordered
                dataSource={deepseekAdvancedConfigData}
                renderItem={(item) => (
                  <List.Item>
                    <Form.Item
                      style={{ width: '100%' }}
                      className="p-0 m-0"
                      name={item.name}
                      label={
                        <div>
                          {`${item.label} ${!form.getFieldValue(item.name) ? "DEFAULT" : ""}`}
                          {item.subtitle && <div style={{ fontSize: 'smaller', color: '#888' }}>{item.subtitle}</div>}
                        </div>
                      }
                      rules={item.rules}
                    >
                      <Slider
                        min={item.rules[0].min}
                        max={item.rules[0].max}
                        step={item.rules[0].step}
                        disabled={!isEditing}
                        onChange={(value) => form.setFieldsValue({ [item.name]: value })}
                        value={form.getFieldValue(item.name)}
                      />
                    </Form.Item>
                  </List.Item>
                )}
              />
            </Col>
          </Row>
        </div>
        <div className="text-end">
          <Button loading={isUpdating} disabled={isUpdating} onClick={handleUpdateClick}>
            {isEditing ? "Update" : "Edit"}
          </Button>
          {isEditing && (
            <>
              <Button className="ms-2" onClick={handleReset}>Reset</Button>
              <Button className="ms-2" danger onClick={() => setIsEditing(!isEditing)}>Cancel</Button>
            </>
          )}
        </div>
      </Form>
      <AccessModal area="advance_parameter" />
    </>
  );
};

export default AdvanceAiParameters;
